import React, { useState, createElement } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const GenericSheetMusicViewer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    id
  } = useParams();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100); // Get item data from location state
  const item = location.state?.item;
  // If no item data, redirect back to library
  // Check if user came from teacher or student route
  if (!item) {
    const referrer = document.referrer;
    const redirectPath = referrer.includes('teacher-sheet-music') ? '/teacher-sheet-music' : '/sheet-music';
    navigate(redirectPath);
    return null;
  }
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = item.pdfUrl;
    link.download = `${item.composer} - ${item.title}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-3423">
      <Navbar data-id="element-3424" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-3425">
        {/* Hero Banner */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-3426">
          {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt={item.title} className="w-full h-64 md:h-80 object-cover" data-id="element-3427" /> : <div className={`w-full h-64 md:h-80 ${isDark ? 'bg-navy-900' : 'bg-gray-200'}`} data-id="element-3428" />}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-3429" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-3430">
            <div className="max-w-7xl mx-auto" data-id="element-3431">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-3432">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-3433" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-3434">
                {item.title}
              </h1>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-3435">
                {item.composer}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-3436">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-3437">
          <div className="flex items-center space-x-2" data-id="element-3438">
            <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-3439">
              <ZoomOutIcon className="h-5 w-5" data-id="element-3440" />
            </button>
            <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3441">
              {zoom}%
            </span>
            <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-3442">
              <ZoomInIcon className="h-5 w-5" data-id="element-3443" />
            </button>
          </div>
          <div className="flex items-center space-x-2" data-id="element-3444">
            <button onClick={handlePrint} className={`flex items-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-3445">
              <PrinterIcon className="h-4 w-4 mr-2" data-id="element-3446" />
              <span className="hidden sm:inline" data-id="element-3447">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-3448">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-3449" />
              <span className="hidden sm:inline" data-id="element-3450">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-3451">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-3452">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-3453">
            <iframe src={`${item.pdfUrl}#view=FitH`} className="w-full h-full" title={`${item.title} Sheet Music`} style={{
            border: 'none',
            transform: `scale(${zoom / 100})`,
            transformOrigin: 'top center',
            transition: 'transform 0.2s ease'
          }} data-id="element-3454" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-3455">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3456">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-3457">
            <div data-id="element-3458">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3459">
                Details
              </h3>
              <dl className="space-y-2" data-id="element-3460">
                <div data-id="element-3461">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3462">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3463">
                    {item.composer}
                  </dd>
                </div>
                <div data-id="element-3464">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3465">
                    Era
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3466">
                    {item.era}
                  </dd>
                </div>
                <div data-id="element-3467">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3468">
                    Difficulty
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3469">
                    {item.difficulty}
                  </dd>
                </div>
                <div data-id="element-3470">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3471">
                    Instruments
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3472">
                    {item.instruments.join(', ')}
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-3473">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3474">
                Tags
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-3475">
                {item.tags.map((tag: string, index: number) => <span key={index} className={`text-sm px-3 py-1 rounded-full ${isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-50 text-blue-600'}`} data-id="element-3476">
                    {tag}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-3477" />
    </div>;
};